import { endOfMonth, endOfWeek, format, isValid, startOfMonth, startOfWeek, subDays, subMonths } from 'date-fns';
import { useMemo, useState } from 'react';

export type DateRangeStr = [string, string];
export type DateRangeKey = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth';

export interface UseDateRangeOptions {
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  initialPreset?: DateRangeKey;
  initialRange?: DateRangeStr;
  fmt?: string;
}

export interface UseDateRange {
  range: DateRangeStr;
  rangeDates: [Date, Date];
  setQuick: (key: DateRangeKey) => void;
  setMonth: (monthDate: Date | null) => void;
  setCustom: (from: Date | null, to: Date | null) => void;
  resetToInitial: () => void;
  selectedQuick?: DateRangeKey;
  formatDate: (d: Date) => string;
}

export function useDateRange(options: UseDateRangeOptions = {}): UseDateRange {
  const { weekStartsOn = 1, initialPreset = 'thisMonth', initialRange, fmt = 'yyyy-MM-dd' } = options;

  const formatDate = (d: Date) => format(d, fmt);

  const computePresets = () => {
    const today = new Date();
    const thisWeekStart = startOfWeek(today, { weekStartsOn });
    const thisWeekEnd = endOfWeek(today, { weekStartsOn });
    const lastWeekEnd = subDays(thisWeekStart, 1);
    const lastWeekStart = startOfWeek(lastWeekEnd, { weekStartsOn });
    const thisMonthStart = startOfMonth(today);
    const thisMonthEnd = endOfMonth(today);
    const lastMonthDate = subMonths(today, 1);
    const lastMonthStart = startOfMonth(lastMonthDate);
    const lastMonthEnd = endOfMonth(lastMonthDate);

    return {
      today: [formatDate(today), formatDate(today)] as DateRangeStr,
      yesterday: [formatDate(subDays(today, 1)), formatDate(subDays(today, 1))] as DateRangeStr,
      thisWeek: [formatDate(thisWeekStart), formatDate(thisWeekEnd)] as DateRangeStr,
      lastWeek: [formatDate(lastWeekStart), formatDate(lastWeekEnd)] as DateRangeStr,
      thisMonth: [formatDate(thisMonthStart), formatDate(thisMonthEnd)] as DateRangeStr,
      lastMonth: [formatDate(lastMonthStart), formatDate(lastMonthEnd)] as DateRangeStr
    };
  };

  const presets = useMemo(computePresets, [weekStartsOn, fmt]);

  const initial = initialRange ?? presets[initialPreset];

  const [range, setRange] = useState<DateRangeStr>(initial);
  const [selectedQuick, setSelectedQuick] = useState<DateRangeKey | undefined>(
    initialRange ? undefined : initialPreset
  );

  const rangeDates = useMemo<[Date, Date]>(() => [new Date(range[0]), new Date(range[1])], [range]);

  const setQuick = (key: DateRangeKey) => {
    setRange(presets[key]);
    setSelectedQuick(key);
  };

  const setMonth = (monthDate: Date | null) => {
    if (!monthDate || !isValid(monthDate)) return;
    const from = startOfMonth(monthDate);
    const to = endOfMonth(monthDate);
    setRange([formatDate(from), formatDate(to)]);
    setSelectedQuick(undefined);
  };

  const setCustom = (from: Date | null, to: Date | null) => {
    if (!from || !to || !isValid(from) || !isValid(to)) return;
    setRange([formatDate(from), formatDate(to)]);
    setSelectedQuick(undefined);
  };

  const resetToInitial = () => {
    setRange(initial);
    setSelectedQuick(initialRange ? undefined : initialPreset);
  };

  return {
    range,
    rangeDates,
    setQuick,
    setMonth,
    setCustom,
    resetToInitial,
    selectedQuick,
    formatDate
  };
}
